import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { OptionsCurrencyDisplay } from './OptionsCurrencyDisplay';
import { OptionsMonetaryText } from './OptionsMonetaryText';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { DASHBOARD_FINANCIAL_VIEW_CURRENCIES } from './optionsDashboardFinancialUtils';

export const OptionsDashboardAccountSummaryCard = ({ model, selectedCurrency, onCurrencyChange }) => {
  const { t } = useOptionsLocale();

  return (
    <Card className="border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] text-white shadow-none" data-testid="options-dashboard-account-summary-card">
      <CardHeader className="space-y-4 pb-3">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/45" data-typography="label" data-testid="options-dashboard-account-summary-label">
              {model.totalLabel}
            </p>
            <CardTitle className="text-4xl font-black tracking-tight sm:text-5xl" data-typography="metric" data-testid="options-dashboard-account-summary-headline">
              <OptionsMonetaryText value={model.totalValue} iconSize={22} />
            </CardTitle>
            {model.totalSecondary ? (
              <p className="text-sm text-white/55 tabular-nums" data-testid="options-dashboard-account-summary-secondary">
                <OptionsMonetaryText value={model.totalSecondary} iconSize={14} />
              </p>
            ) : null}
            {model.valuationNote ? (
              <p className="text-sm text-[#FBBF24]" data-testid="options-dashboard-account-summary-valuation-note">{model.valuationNote}</p>
            ) : null}
          </div>

          <div className="w-full max-w-[290px] space-y-2 lg:w-auto" data-testid="options-dashboard-account-visualization-selector">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/35" data-typography="label" data-testid="options-dashboard-account-visualization-label">
              {t('dashboard.visualizationLabel')}
            </p>
            <div className="inline-flex w-full flex-wrap items-center gap-2 rounded-[18px] border border-white/10 bg-[#11161E] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] lg:w-auto" data-testid="options-dashboard-account-visualization-tabs">
              {DASHBOARD_FINANCIAL_VIEW_CURRENCIES.map((currency) => {
                const isActive = selectedCurrency === currency;
                return (
                  <button
                    key={currency}
                    type="button"
                    onClick={() => onCurrencyChange(currency)}
                    data-testid={`options-dashboard-account-visualization-${currency}`}
                    className={`inline-flex min-h-[42px] min-w-[98px] items-center justify-center rounded-[14px] px-4 py-2.5 text-sm font-semibold transition-all duration-200 font-body ${isActive ? 'bg-[#202A38] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]' : 'text-white/70 hover:bg-white/[0.04] hover:text-white'}`}
                    data-typography="nav"
                  >
                    <OptionsCurrencyDisplay currencyKey={currency} compact iconSize={18} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-3" data-testid="options-dashboard-account-assets-section">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40" data-typography="label" data-testid="options-dashboard-account-assets-label">
            {model.assetsLabel}
          </p>

          <div className="overflow-hidden rounded-[24px] border border-white/10 bg-black/20" data-testid="options-dashboard-account-assets-list">
            {model.assets.map((asset, index) => (
              <div
                key={asset.key}
                className={`grid gap-4 px-4 py-4 md:grid-cols-[minmax(0,1.2fr),minmax(0,0.8fr),minmax(0,0.8fr)] md:items-center ${index !== model.assets.length - 1 ? 'border-b border-white/8' : ''}`}
                data-testid={`options-dashboard-account-asset-row-${asset.key.toLowerCase()}`}
              >
                <div className="flex items-center gap-3" data-testid={`options-dashboard-account-asset-meta-${asset.key.toLowerCase()}`}>
                  <OptionsAssetIcon assetKey={asset.currency} size={20} className="shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-white" data-testid={`options-dashboard-account-asset-name-${asset.key.toLowerCase()}`}>{asset.name}</p>
                    <p className="text-xs uppercase tracking-[0.18em] text-white/42" data-testid={`options-dashboard-account-asset-code-${asset.key.toLowerCase()}`}>{asset.code}</p>
                  </div>
                </div>

                <div className="md:text-right" data-testid={`options-dashboard-account-asset-native-${asset.key.toLowerCase()}`}>
                  <p className="text-lg font-semibold text-white tabular-nums">{asset.nativeDisplay}</p>
                </div>

                <div className="md:text-right" data-testid={`options-dashboard-account-asset-usd-${asset.key.toLowerCase()}`}>
                  <p className={`text-sm font-semibold tabular-nums ${asset.usdToneClass || 'text-white/78'}`}>{asset.usdDisplay}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OptionsDashboardAccountSummaryCard;